import type { TaskItem, TaskList } from "@/api/mailApi";
import { useCalendarSettings } from "@/api/hooks";
import { DEFAULT_TIME_ZONE, isValidTimeZone, zonedParts, zonedToInstant } from "@/lib/timezone";

/**
 * Pure helpers for the To Do-style task views: the user's day in their
 * calendar time zone, smart-list membership, due buckets, sorting and the
 * repeat-rule (RRULE subset) model shared with the BFF's TaskRecurrence.
 */

export type SmartListId = "myday" | "important" | "planned" | "flagged" | "tasks";
/** A smart list id, or a user list's id. */
export type ListKey = SmartListId | string;

export const SMART_LISTS: { id: SmartListId; name: string }[] = [
    { id: "myday", name: "My Day" },
    { id: "important", name: "Important" },
    { id: "planned", name: "Planned" },
    { id: "flagged", name: "Flagged email" },
    { id: "tasks", name: "Tasks" }
];

export function isSmartList(key: string): key is SmartListId {
    return SMART_LISTS.some((s) => s.id === key);
}

export function listName(key: ListKey, lists: TaskList[] | undefined): string {
    const smart = SMART_LISTS.find((s) => s.id === key);
    if (smart) return smart.name;
    return lists?.find((l) => l.id === key)?.name ?? "Tasks";
}

/** The calendar setting's zone (default Asia/Kolkata). */
export function useTaskTimeZone(): string {
    const tz = useCalendarSettings().data?.timeZone;
    return tz && isValidTimeZone(tz) ? tz : DEFAULT_TIME_ZONE;
}

// ── Day keys ("YYYY-MM-DD" wall-clock dates in the user's zone) ──

const pad = (n: number) => String(n).padStart(2, "0");

export function makeKey(y: number, m: number, d: number): string {
    const dt = new Date(Date.UTC(y, m - 1, d));
    return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

export function keyParts(key: string): { y: number; m: number; d: number } {
    const [y = 1970, m = 1, d = 1] = key.split("-").map(Number);
    return { y, m, d };
}

export function dayKey(instant: Date, tz: string): string {
    const p = zonedParts(instant, tz);
    return makeKey(p.year, p.month, p.day);
}

export function todayKey(tz: string): string {
    return dayKey(new Date(), tz);
}

export function addDaysKey(key: string, days: number): string {
    const { y, m, d } = keyParts(key);
    return makeKey(y, m, d + days);
}

/** ISO weekday 1 = Monday … 7 = Sunday. */
export function weekdayOfKey(key: string): number {
    const { y, m, d } = keyParts(key);
    return ((new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7) + 1;
}

export function keyToInstant(key: string, tz: string, hour = 0, minute = 0): Date {
    const { y, m, d } = keyParts(key);
    return zonedToInstant(y, m, d, hour, minute, tz);
}

/** "YYYY-MM-DDTHH:mm" for <input type="datetime-local"> in the user's zone. */
export function toLocalInput(iso: string | null, tz: string): string {
    if (!iso) return "";
    const p = zonedParts(new Date(iso), tz);
    return `${makeKey(p.year, p.month, p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

export function fromLocalInput(value: string, tz: string): string | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
    if (!m) return null;
    return zonedToInstant(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]), tz).toISOString();
}

export function hasTimeOfDay(iso: string, tz: string): boolean {
    const p = zonedParts(new Date(iso), tz);
    return p.hour !== 0 || p.minute !== 0;
}

// ── Smart lists ──

export function isInMyDay(t: TaskItem, today: string): boolean {
    return t.myDayDate === today;
}

export function inList(t: TaskItem, key: ListKey, today: string): boolean {
    switch (key) {
        case "myday": return isInMyDay(t, today);
        case "important": return t.isImportant;
        case "planned": return Boolean(t.dueAt || t.reminderAt);
        case "flagged": return t.source === "flagged";
        case "tasks": return t.listId === null && t.source !== "flagged";
        default: return t.listId === key;
    }
}

export type DueBucket = "overdue" | "today" | "tomorrow" | "later" | "none";

export const DUE_BUCKETS: { id: DueBucket; label: string }[] = [
    { id: "overdue", label: "Overdue" },
    { id: "today", label: "Today" },
    { id: "tomorrow", label: "Tomorrow" },
    { id: "later", label: "Later" },
    { id: "none", label: "No date" }
];

export function dueBucket(t: TaskItem, tz: string, today: string): DueBucket {
    if (!t.dueAt) return "none";
    const k = dayKey(new Date(t.dueAt), tz);
    if (k < today) return "overdue";
    if (k === today) return "today";
    if (k === addDaysKey(today, 1)) return "tomorrow";
    return "later";
}

export function isOverdue(t: TaskItem, tz: string, today: string): boolean {
    return !t.isCompleted && dueBucket(t, tz, today) === "overdue";
}

/** "Today", "Tomorrow", "Yesterday", "Mon, 15 Sep" — plus the time when it isn't midnight. */
export function formatDay(iso: string, tz: string, today: string, withTime = true): string {
    const at = new Date(iso);
    const k = dayKey(at, tz);
    const day = k === today ? "Today"
        : k === addDaysKey(today, 1) ? "Tomorrow"
        : k === addDaysKey(today, -1) ? "Yesterday"
        : at.toLocaleDateString(undefined, {
            weekday: "short", day: "numeric", month: "short",
            year: k.slice(0, 4) === today.slice(0, 4) ? undefined : "numeric",
            timeZone: tz
        });
    if (!withTime || !hasTimeOfDay(iso, tz)) return day;
    return `${day} ${at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz })}`;
}

// ── Sorting ──

export type TaskSort = "due" | "importance" | "created" | "alpha";

export const TASK_SORTS: { id: TaskSort; label: string }[] = [
    { id: "due", label: "Due date" },
    { id: "importance", label: "Importance" },
    { id: "created", label: "Creation date" },
    { id: "alpha", label: "Alphabetically" }
];

export function sortTasks(tasks: TaskItem[], sort: TaskSort): TaskItem[] {
    const byCreated = (a: TaskItem, b: TaskItem) => b.createdAt.localeCompare(a.createdAt);
    const byDue = (a: TaskItem, b: TaskItem) => {
        if (a.dueAt && b.dueAt) return a.dueAt.localeCompare(b.dueAt);
        if (a.dueAt) return -1;
        if (b.dueAt) return 1;
        return 0;
    };
    const cmp: Record<TaskSort, (a: TaskItem, b: TaskItem) => number> = {
        due: (a, b) => byDue(a, b) || byCreated(a, b),
        importance: (a, b) => Number(b.isImportant) - Number(a.isImportant) || byDue(a, b) || byCreated(a, b),
        created: byCreated,
        alpha: (a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }) || byCreated(a, b)
    };
    return [...tasks].sort(cmp[sort]);
}

// ── Repeat rules ──

export type RepeatFreq = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
export const DAY_CODES = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;
export const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export interface RepeatRule {
    freq: RepeatFreq;
    interval: number;
    /** Monday-first codes. */
    byDay: string[];
    byMonthDay: number | null;
}

export function parseRule(rule: string | null | undefined): RepeatRule | null {
    if (!rule) return null;
    const out: RepeatRule = { freq: "DAILY", interval: 1, byDay: [], byMonthDay: null };
    let hasFreq = false;
    for (const part of rule.split(";")) {
        const [k, v = ""] = part.split("=");
        const key = (k ?? "").trim().toUpperCase();
        const value = v.trim().toUpperCase();
        if (key === "FREQ" && ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"].includes(value)) {
            out.freq = value as RepeatFreq;
            hasFreq = true;
        } else if (key === "INTERVAL") out.interval = Math.max(1, Number(value) || 1);
        else if (key === "BYDAY") out.byDay = value.split(",").filter((d) => (DAY_CODES as readonly string[]).includes(d));
        else if (key === "BYMONTHDAY") out.byMonthDay = Number(value) || null;
    }
    return hasFreq ? out : null;
}

export function buildRule(r: RepeatRule): string {
    let s = `FREQ=${r.freq}`;
    if (r.interval > 1) s += `;INTERVAL=${r.interval}`;
    if (r.freq === "WEEKLY" && r.byDay.length > 0) {
        s += `;BYDAY=${DAY_CODES.filter((d) => r.byDay.includes(d)).join(",")}`;
    }
    if (r.freq === "MONTHLY" && r.byMonthDay) s += `;BYMONTHDAY=${r.byMonthDay}`;
    return s;
}

export const WEEKDAYS_RULE = "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR";

const ordinal = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

export function describeRule(rule: string | null | undefined): string {
    const r = parseRule(rule);
    if (!r) return "";
    if (buildRule(r) === WEEKDAYS_RULE) return "Weekdays";
    const unit = { DAILY: "day", WEEKLY: "week", MONTHLY: "month", YEARLY: "year" }[r.freq];
    const base = r.interval === 1
        ? { DAILY: "Daily", WEEKLY: "Weekly", MONTHLY: "Monthly", YEARLY: "Yearly" }[r.freq]
        : `Every ${r.interval} ${unit}s`;
    if (r.freq === "WEEKLY" && r.byDay.length > 0) {
        return `${base} on ${r.byDay.map((d) => DAY_SHORT[DAY_CODES.indexOf(d as (typeof DAY_CODES)[number])]).join(", ")}`;
    }
    if (r.freq === "MONTHLY" && r.byMonthDay) return `${base} on the ${ordinal(r.byMonthDay)}`;
    return base;
}

/** First day on/after `from` that the rule lands on — used to give a new repeating task its due date. */
export function firstOccurrenceKey(r: RepeatRule, from: string): string {
    if (r.freq === "WEEKLY" && r.byDay.length > 0) {
        for (let i = 0; i < 7; i++) {
            const k = addDaysKey(from, i);
            if (r.byDay.includes(DAY_CODES[weekdayOfKey(k) - 1] ?? "")) return k;
        }
    }
    if (r.freq === "MONTHLY" && r.byMonthDay) {
        const { y, m, d } = keyParts(from);
        const days = (yy: number, mm: number) => new Date(Date.UTC(yy, mm, 0)).getUTCDate();
        if (d <= r.byMonthDay) return makeKey(y, m, Math.min(r.byMonthDay, days(y, m)));
        const ny = m === 12 ? y + 1 : y;
        const nm = m === 12 ? 1 : m + 1;
        return makeKey(ny, nm, Math.min(r.byMonthDay, days(ny, nm)));
    }
    return from;
}

export function stepProgress(t: TaskItem): string | null {
    if (t.steps.length === 0) return null;
    return `${t.steps.filter((s) => s.isCompleted).length} of ${t.steps.length}`;
}

export function newId(): string {
    try {
        return crypto.randomUUID().replace(/-/g, "");
    } catch {
        return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
    }
}
